/**
 * useLevel — the DeviceMotion subscription behind the level indicator (level.ts holds the maths).
 *
 * Listens to `devicemotion` only while `enabled` (premises still, camera live, grid on, motion
 * permission granted or not required). Every sample feeds the smoother; React state updates at
 * ~10 Hz (§D) so the line moves without re-rendering the viewfinder sixty times a second.
 *
 * Reads NOTHING from the session's ImuTracer and writes nothing to it: stills bind no IMU header
 * today and still do not. Denied / unsupported ⇒ the caller never enables this hook, and the
 * level is simply absent — never faked.
 */

import { useEffect, useState } from 'react';
import { type LevelReading, LevelSmoother, rollFromGravity, screenAngle } from './level';

const RENDER_INTERVAL_MS = 100;

export function useLevel(enabled: boolean): LevelReading | null {
  const [reading, setReading] = useState<LevelReading | null>(null);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') {
      setReading(null);
      return undefined;
    }
    const smoother = new LevelSmoother();
    let lastRender = 0;
    const onMotion = (e: Event): void => {
      const a = (e as DeviceMotionEvent).accelerationIncludingGravity;
      if (!a || typeof a.x !== 'number' || typeof a.y !== 'number') return;
      const next = smoother.push(rollFromGravity({ x: a.x, y: a.y, z: typeof a.z === 'number' ? a.z : 0 }, screenAngle()));
      const now = Date.now();
      if (now - lastRender < RENDER_INTERVAL_MS) return;
      lastRender = now;
      setReading(next);
    };
    window.addEventListener('devicemotion', onMotion);
    return () => {
      window.removeEventListener('devicemotion', onMotion);
      setReading(null);
    };
  }, [enabled]);

  return reading;
}
